import {useContext, useState} from 'react';
import {AxiosError} from 'axios';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import Helpers from '@/helpers/helpers';
import CampaignConfigTable from '@/components/pages/settings/telephony/CampaignConfigTable';
import CampaignConfigForm, {
  CampaignConfigFormResult,
} from '@/components/pages/settings/telephony/CampaignConfigForm';
import {CampaignConfigResponse} from '@/api/dto/connect';

export default function CampaignConfigsTab() {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CampaignConfigResponse | null>(null);

  const {
    data: campaignConfigs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['campaignConfigs'],
    queryFn: () => carecoApi?.getCampaignConfigs(),
    enabled: !!carecoApi,
  });

  const {
    mutate: saveCampaignConfig,
    isPending: isSaving,
    error: saveError,
    reset: resetSaveError,
  } = useMutation({
    mutationFn: async (result: CampaignConfigFormResult) => {
      if (!carecoApi) return;
      if (result.mode === 'create') {
        return carecoApi.createCampaignConfig({
          rosterId: result.rosterId,
          campaignId: result.campaignId,
          voicemailConfigId: result.voicemailConfigId
        });
      }
      return carecoApi.updateCampaignConfig(result.id, {
        campaignId: result.campaignId,
        voicemailConfigId: result.voicemailConfigId
      });
    },
    onSuccess(_data, variables) {
      void queryClient.invalidateQueries({ queryKey: ['campaignConfigs'] });
      void queryClient.invalidateQueries({ queryKey: ['voicemailConfigs'] }); // to update 'usedBy' field in voicemail table
      addAlert({
        type: ToastType.SUCCESS,
        content: variables.mode === 'create'
          ? 'Campaign config saved successfully.'
          : 'Campaign config updated successfully.',
      });
      setIsFormOpen(false);
      setEditTarget(null);
    },
    onError(error: AxiosError) {
      const status = error.response?.status;
      // 400, 404, and 409 are surfaced inline in the form. Everything else gets a toast.
      if (status === 400 || status === 404 || status === 409) {
        return;
      }
      Helpers.alertOnError(`Failed to save campaign config. ${Helpers.traceId(error)}`, error);
    },
  });

  const { mutate: deactivateCampaignConfig, isPending: isDeactivating } = useMutation({
    mutationFn: async (id: number) => carecoApi?.deactivateCampaignConfig(id),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ['campaignConfigs'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Campaign config deactivated.',
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to deactivate campaign config. ${Helpers.traceId(error)}`, error);
    },
  });

  const closeForm = () => {
    setIsFormOpen(false);
    setEditTarget(null);
    resetSaveError();
  };

  return (
    <>
      <CampaignConfigTable
        campaignConfigs={campaignConfigs}
        isLoading={isLoading}
        error={error}
        onEdit={(config) => {
          setEditTarget(config);
          setIsFormOpen(true);
        }}
        onDeactivate={(id) => deactivateCampaignConfig(id)}
        isDeactivating={isDeactivating}
        onAddClick={() => {
          setEditTarget(null);
          setIsFormOpen(true);
        }}
      />
      <CampaignConfigForm
        isOpen={isFormOpen}
        close={closeForm}
        onSave={(result) => saveCampaignConfig(result)}
        isSaving={isSaving}
        saveError={saveError as AxiosError | null}
        resetError={resetSaveError}
        existingConfigs={campaignConfigs}
        editTarget={editTarget}
      />
    </>
  );
}
